import BookingForm from "./components/BookingForm";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import RoomDetailsPage from "./components/RoomImagesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import CancellationRefundPolicy from "./pages/CancellationRefundPolicy";
import RoomsPage from "./pages/RoomsPage";
import ContactUs from "./pages/ContactUs";
import AboutUs from "./pages/AboutUs";
import BookingConfirmation from "./pages/BookingConfirmation";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./components/HomePage";
import { callBackend } from "./backend/callBackend";
import { useDispatch, useSelector } from "react-redux";
import { setHotelData } from "./redux/hotelSlice";
import { setConfig } from "./redux/configSlice";
import { setMediaData, setMediaLoading } from "./redux/mediaSlice";
import { fetchWebsiteMedia } from "./backend/mediaService";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "./redux/store";
import { useRef, useEffect } from "react";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const hotelData = useSelector((state) => state.hotel.data);
  const dispatch = useDispatch();
  const ONE_HOUR_MS = 60 * 60 * 1000;

  const hotelFetchRef = useRef(false);
  const configFetchRef = useRef(false);
  const mediaFetchRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const getHostname = () => {
      const host = window.location.hostname || "";
      if (
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.includes("ganeshholidayhomes") ||
        host.includes("ganesh-holiday-homes")
      ) {
        return "ganeshholidayhomes";
      }
      return host || "ganeshholidayhomes";
    };

    const isHotelExpired = (data) => {
      if (!data || !data.__persistedAt) return true;
      return Date.now() - data.__persistedAt > ONE_HOUR_MS;
    };

    const fetchHotel = async () => {
      if (hotelFetchRef.current) return;
      hotelFetchRef.current = true;

      const hostname = getHostname();
      let data = null;

      try {
        const publicUrl = process.env.PUBLIC_URL || "";
        let res = await fetch(`${publicUrl}/cache/brand-${hostname}.json`, {
          cache: "force-cache",
        });

        let contentType = res.headers.get("content-type");
        if (!res.ok || !contentType?.includes("application/json")) {
          // Fallback to default brand cache file if specific host cache not found
          res = await fetch(`${publicUrl}/cache/brand-ganeshholidayhomes.json`, {
            cache: "force-cache",
          });
          contentType = res.headers.get("content-type");
        }

        if (res.ok && contentType?.includes("application/json")) {
          data = await res.json();
          console.log("📦 Loaded brand from CDN cache");
        } else {
          throw new Error("Cache miss");
        }
      } catch {
        try {
          const response = await callBackend(
            "backendBookingService",
            "fetchBrand",
            { hostname }
          );
          if (response?.success && response?.data) {
            data = response.data;
            console.log("☁️ Loaded brand from backend");
          }
        } catch (e) {
          console.warn("⚠️ Both cache & backend failed", e);
        }
      }

      if (!isMounted) return;

      dispatch(
        setHotelData(
          data ?? {
            name: "Ganesh Holiday Homes",
            email: "ganeshholidayhomes@gmail.com",
          }
        )
      );

      // Ping backend warm-up once
      callBackend("backendBookingService", "pingServer").catch(() => {});
    };

    const fetchBackendConfig = async () => {
      if (configFetchRef.current) return;
      configFetchRef.current = true;

      try {
        const response = await callBackend(
          "backendBookingService",
          "fetchConfig",
          {}
        );
        if (response?.data && isMounted) {
          dispatch(setConfig(response.data));
          console.log("⚙️ Loaded config & price_map from backend");
        }
      } catch (err) {
        console.warn("⚠️ Failed to fetch config from backend:", err);
      }
    };

    const fetchWebsiteMediaData = async () => {
      if (mediaFetchRef.current) return;
      mediaFetchRef.current = true;

      try {
        dispatch(setMediaLoading(true));
        const mediaResult = await fetchWebsiteMedia({ isVisible: true });
        if (mediaResult && isMounted) {
          dispatch(setMediaData(mediaResult));
          console.log("📸 Loaded website room media & gallery from backend");
        }
      } catch (err) {
        console.warn("⚠️ Failed to fetch website media from backend:", err);
      }
    };

    if (isHotelExpired(hotelData)) {
      fetchHotel();
    }

    // Always fetch fresh config & price_map from backend on page load
    fetchBackendConfig();

    // Fetch latest room media & gallery from backend
    fetchWebsiteMediaData();

    return () => {
      isMounted = false;
    };
  }, []); // Run on mount once per page load

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/rooms" element={<RoomsPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/booking" element={<BookingForm />} />
        <Route path="/booking-confirmation" element={<BookingConfirmation />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-and-conditions" element={<Terms />} />
        <Route path="/terms-conditions" element={<Terms />} />
        <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
        <Route path="/refund-policy" element={<CancellationRefundPolicy />} />
        <Route path="/images/:roomType?" element={<RoomDetailsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <PersistGate persistor={persistor} loading={null}>
        <AppContent />
      </PersistGate>
    </HelmetProvider>
  );
}
